import { emailReminderDescription } from './EmailReminderDialog.locstring.json';
import { closeText } from 'owa-locstrings/lib/strings/closetext.locstring.json';
import { emailReminderHeader, addAnotherEmailReminderButtonLabel } from '../strings.locstring.json';
import loc from 'owa-localize';
import EmailReminderEdit from './EmailReminderEdit';
import EmailReminderRow from './EmailReminderRow';
import { addNewEmailReminder } from '../actions/draftInboxReminderActions';
import { hideEmailReminderDialog } from '../actions/emailReminderDialogActions';
import getInboxReminderDraft from '../selectors/getInboxReminderDraft';
import EmailReminderEntrySource from '../store/schema/EmailReminderEntrySource';
import isNewReminder from '../utils/isNewReminder';
import { observer } from 'mobx-react';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { logUsage } from 'owa-analytics';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import { keydownHandler } from 'owa-hotkeys';
import InboxReminderType from 'owa-service/lib/contract/InboxReminderType';
import { Modal } from 'office-ui-fabric-react/lib/Modal';
import { getId } from 'office-ui-fabric-react/lib/Utilities';

import * as React from 'react';

const styles = require('./EmailReminderDialog.scss');
const classnames = require('classnames');

export interface EmailReminderDialogProps {
    event: CalendarEvent;
    colorScheme: ColorScheme;
    entrySource: EmailReminderEntrySource;
    inboxRemindersToRender: InboxReminderType[] | undefined;
}

@observer
export default class EmailReminderDialog extends React.Component<EmailReminderDialogProps, {}> {
    private MAX_EMAIL_REMINDERS_PER_EVENT = 5;

    render() {
        const titleId = getId('dialogTitle');

        return (
            <Modal
                isOpen={true}
                isBlocking={false}
                onDismiss={this.onDismiss}
                containerClassName={classnames(styles.remindersDialog, 'customScrollBar')}
                titleAriaId={titleId}>
                <div className={styles.headerSection}>
                    <span id={titleId} className={styles.title}>
                        {loc(emailReminderHeader)}
                    </span>
                    <IconButton
                        ariaLabel={loc(closeText)}
                        className={styles.closeButton}
                        iconProps={{ iconName: ControlIcons.ChromeClose }}
                        onClick={this.onCloseButtonClicked}
                    />
                </div>
                <span className={styles.subTitle}>{loc(emailReminderDescription)}</span>
                {<div className={styles.remindersList}>{this.tryRenderEmailReminders()}</div>}
                {this.shouldRenderAddEmailReminderButton() && this.renderAddEmailReminderButton()}
            </Modal>
        );
    }

    private shouldRenderAddEmailReminderButton = (): boolean => {
        // Show 'Add new email reminder' button if there is no reminder being editted
        return (
            getInboxReminderDraft() === null &&
            this.props.inboxRemindersToRender.length < this.MAX_EMAIL_REMINDERS_PER_EVENT
        );
    };

    private tryRenderEmailReminders = (): JSX.Element[] => {
        const inboxRemindersToRender = this.props.inboxRemindersToRender;
        const draftReminder = getInboxReminderDraft();

        // Do not render the list if
        // - the event does not have any existing inbox reminders
        // - and if there is no draft reminder
        if (inboxRemindersToRender.length === 0 && !draftReminder) {
            return null;
        }

        const rowsToRender = inboxRemindersToRender.map(reminder => {
            if (draftReminder && draftReminder.Id === reminder.Id) {
                // if existing reminder is currently being editted, render an editted reminder
                return (
                    <EmailReminderEdit
                        key={reminder.Id}
                        colorScheme={this.props.colorScheme}
                        event={this.props.event}
                        entrySource={this.props.entrySource}
                        isReminderNew={false}
                    />
                );
            } else {
                // Otherwise render a normal reminder row
                return (
                    <EmailReminderRow
                        key={reminder.Id}
                        event={this.props.event}
                        entrySource={this.props.entrySource}
                        inboxReminder={reminder}
                    />
                );
            }
        });

        // if there is a new reminder draft, render it after the inbox reminder list
        if (draftReminder && isNewReminder(draftReminder, this.props.inboxRemindersToRender)) {
            rowsToRender.push(
                <EmailReminderEdit
                    key={draftReminder.Id}
                    event={this.props.event}
                    colorScheme={this.props.colorScheme}
                    entrySource={this.props.entrySource}
                    isReminderNew={true}
                />
            );
        }

        return rowsToRender;
    };

    private renderAddEmailReminderButton = (): JSX.Element => {
        const textColor = this.props.colorScheme.calPrimary;
        const textColorHovered = this.props.colorScheme.calDark;
        const textColorPressed = this.props.colorScheme.calDarker;
        return (
            <ActionButton
                text={loc(addAnotherEmailReminderButtonLabel)}
                iconProps={{ iconName: ControlIcons.Add }}
                styles={{
                    root: { paddingLeft: 0 },
                    icon: { color: textColor, marginLeft: 0 },
                    iconHovered: { color: textColorHovered },
                    iconPressed: { color: textColorPressed },
                    label: { color: textColor },
                    labelHovered: { color: textColorHovered },
                    labelChecked: { color: textColorPressed },
                }}
                className={styles.addReminderButton}
                onClick={this.onAddEmailReminderButtonClicked}
            />
        );
    };

    private onAddEmailReminderButtonClicked = () => {
        addNewEmailReminder(
            this.props.event.IsAllDayEvent,
            this.props.event.IsOrganizer,
            this.props.event.CalendarItemType
        );

        logUsage('AddEmailReminderButtonClicked');
    };

    private onCloseButtonClicked() {
        hideEmailReminderDialog();
    }

    @keydownHandler('esc', true /* stopPropagation */)
    onEscape(evt: KeyboardEvent) {
        hideEmailReminderDialog();
    }

    private onDismiss() {
        hideEmailReminderDialog();
    }
}
